import { useMemo } from 'react'
import moment from 'moment'

import MicroVolumeStimulusTypeIcon from 'components/Program/MicroVolumeConfig/MicroVolumeStimulusTypeIcon'

const FULL_HEIGHT = 'h-[75px] 2xl:h-[100px]'
const GHOST_MICRO_VOLUME_ID = 'GHOST_MICRO_VOLUME'

export default function MicroVolumeTimelineDisplay({
    macroVolumeFocusTypeHashMap = {},
    microVolumeStimulusTypeHashMap = {},
    mesoVolumes = [],
    renderRangeStart,
    renderRangeEnd,
    daySpacing,
    DateToTrackLeftX = () => { return 0 },
    DateToWeekIndex = () => { return 0 },
    HandleMicroVolumeClick = () => {},
}) {

    const GetMicroVolumeBarStyle = (taperLevel, isGhostWeek = false) => {
        var style = 'w-full flex items-end rounded transition-shadow ring-offset-2 ring-offset-white cursor-pointer '

        if (isGhostWeek) {
            style += FULL_HEIGHT + ' bg-gray-300 '
            return style
        }

        if (taperLevel === 'taper-none') style += FULL_HEIGHT + ' '
        if (taperLevel === 'taper-one') style += 'h-14 2xl:h-20 '
        if (taperLevel === 'taper-two') style += 'h-10 2xl:h-14 '
        if (taperLevel === 'taper-three') style += 'h-6 2xl:h-8 '

        return style
    }

    const microVolumeBars = useMemo(() => {
        if (!mesoVolumes?.length || daySpacing <= 0) {
            return []
        }

        const newMicroVolumeBars = []
        let programWeekIndex = 0

        mesoVolumes.forEach((mesoVolume) => {
            const microVolumeConfigs = mesoVolume.microVolumes ?? []

            microVolumeConfigs.forEach((microVolumeConfig) => {
                const isGhostWeek = microVolumeConfig.microVolumeId === GHOST_MICRO_VOLUME_ID
                const startDate = moment(microVolumeConfig.startDate).startOf('day')
                const endDate = moment(microVolumeConfig.endDate).startOf('day')

                if (
                    !startDate.isValid()
                    || !endDate.isValid()
                    || endDate.isBefore(renderRangeStart, 'day')
                    || startDate.isAfter(renderRangeEnd, 'day')
                ) {
                    if (!isGhostWeek) {
                        programWeekIndex += 1
                    }
                    return
                }

                const left = DateToTrackLeftX(startDate)
                const width = DateToTrackLeftX(endDate.clone().add(1, 'day')) - left

                newMicroVolumeBars.push({
                    key: microVolumeConfig.microVolumeId ?? microVolumeConfig.weekOfTheYearKey ?? `program-week-${programWeekIndex}`,
                    left,
                    width,
                    weekIndex: DateToWeekIndex(startDate),
                    programWeekIndex,
                    isGhostWeek,
                    microVolumeConfig,
                    macroVolumePhaseTypeId: mesoVolume.macroVolumePhaseTypeId,
                    macroVolumeFocusTypeId: mesoVolume.macroVolumeFocusTypeId,
                })

                if (!isGhostWeek) {
                    programWeekIndex += 1
                }
            })
        })

        return newMicroVolumeBars
    }, [
        renderRangeStart,
        renderRangeEnd,
        mesoVolumes,
        daySpacing,
        DateToTrackLeftX,
        DateToWeekIndex,
    ])

    return (
        <div className='relative'>
            {/* Absolute bars don't contribute height; this in-flow twin sets track height. */}
            <div className='invisible pointer-events-none select-none w-min' aria-hidden>
                <div className={FULL_HEIGHT} />
            </div>

            {microVolumeBars.map((microVolumeBar) => {
                const isGhostWeek = microVolumeBar.isGhostWeek
                const macroFocusTypeColor = isGhostWeek ? undefined : macroVolumeFocusTypeHashMap?.[microVolumeBar.macroVolumeFocusTypeId]?.colorHex ?? '#e3e3e3'
                const iconKey = isGhostWeek ? null : microVolumeStimulusTypeHashMap?.[microVolumeBar.microVolumeConfig.microVolumeStimulusTypeId]?.iconKey
                const microVolumeBarStyle = GetMicroVolumeBarStyle(microVolumeBar.microVolumeConfig.taperLevel, isGhostWeek)

                return (
                    <div
                        key={microVolumeBar.key}
                        className='absolute bottom-0 px-[1.5px] 2xl:px-[2px] pointer-events-auto'
                        style={{
                            left: `${microVolumeBar.left}px`,
                            width: `${microVolumeBar.width}px`,
                        }}
                    >
                        <div
                            className='group relative flex flex-col items-center justify-end'
                            onClick={() => HandleMicroVolumeClick(microVolumeBar)}
                        >
                            <div
                                className={microVolumeBarStyle}
                                style={isGhostWeek ? undefined : { backgroundColor: macroFocusTypeColor }}
                            >
                                {!isGhostWeek && <MicroVolumeStimulusTypeIcon iconKey={iconKey} />}
                            </div>
                        </div>
                    </div>
                )
            })}
        </div>
    )
}
